import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';

import {
    AddConditionNoteRequest,
    AttachConditionFileRequest,
    ConditionWatcherRequest,
    DeleteConditionRequest,
    EditConditionRequest,
    ResolveConditionRequest,
} from '@/components/pages/Applicant/components/ApplicantConditions/ApplicantConditions.types';

import {
    ApplicationConditionsRequest,
    deleteApplicantCondition,
    deleteConditionWatcher,
    postAddConditionWatcher,
    postApplicantConditionNote,
    postApplicationConditions,
    postAttachConditionFile,
    postResolveApplicantCondition,
    putApplicantCondition,
} from '../endpoints/applicantConditions';

export const CONDITION_WATCHER_MUTATION_KEY = ['condition-watcher'] as const;

// Fire-and-forget so a dialog closes without waiting on the refetch; invalidateQueries doesn't reject.
const invalidateConditions = (queryClient: QueryClient, applicantId: string) => {
    queryClient
        .invalidateQueries({ queryKey: ['applicant-conditions', applicantId] })
        .catch(() => {});
};

export const useAddConditionNoteMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: AddConditionNoteRequest) => postApplicantConditionNote(request),
        onSuccess: (_data, { applicantId }) => invalidateConditions(queryClient, applicantId),
    });
};

export const useAddConditionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: ApplicationConditionsRequest) => postApplicationConditions(request),
        onSuccess: (_data, { applicantId }) => invalidateConditions(queryClient, applicantId),
    });
};

export const useResolveConditionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: ResolveConditionRequest) => postResolveApplicantCondition(request),
        onSuccess: (_data, { applicantId }) => invalidateConditions(queryClient, applicantId),
    });
};

export const useDeleteConditionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: DeleteConditionRequest) => deleteApplicantCondition(request),
        onSuccess: (_data, { applicantId }) => invalidateConditions(queryClient, applicantId),
    });
};

export const useEditConditionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: EditConditionRequest) => putApplicantCondition(request),
        onSuccess: (_data, { applicantId }) => invalidateConditions(queryClient, applicantId),
    });
};

export const useAddConditionWatcherMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: CONDITION_WATCHER_MUTATION_KEY,
        mutationFn: (request: ConditionWatcherRequest) => postAddConditionWatcher(request),
        onSuccess: (_data, { applicantId }) =>
            queryClient.invalidateQueries({ queryKey: ['applicant-conditions', applicantId] }),
    });
};

export const useRemoveConditionWatcherMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: CONDITION_WATCHER_MUTATION_KEY,
        mutationFn: (request: ConditionWatcherRequest) => deleteConditionWatcher(request),
        onSuccess: (_data, { applicantId }) =>
            queryClient.invalidateQueries({ queryKey: ['applicant-conditions', applicantId] }),
    });
};

export const useAttachConditionFileMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: AttachConditionFileRequest) => postAttachConditionFile(request),
        onSuccess: (_data, { applicantId }) => invalidateConditions(queryClient, applicantId),
    });
};
